import { z } from "zod";
import { CommonErrorType } from "./commontypes";
export const ServiceMinimumStringLength = 1;
export const ServiceMinimumSessionIdLength = 14;
export const ServiceMinimumObjectIdLength = 36; // optional prefix + uuid
export const ServiceMinimumMobileNumberLength = 12;
export const ServiceMinimumVerificationCodeLength = 6;
export const ServiceMinimumPostalCodeValidationLength = 4;
export const ServiceMinimumCCTypeLength = 1;
export const ServiceMinimumCCValidationLength = 4;
export const ServiceMinimumCCIdentificationLength = 4;
export const ServiceMinimumPaymentNonce = ServiceMinimumStringLength;
export const ServiceMinimumDeviceData = ServiceMinimumStringLength;
export const ServiceMinimumCurrencyLength = 3;
// BUGBUG: Need specific validation from KEXP donor services
export const ServiceMinimumBraintreeIdLength = 6;
export const ServiceErrorType = z.nativeEnum(CommonErrorType);
export const ServiceError = z.object({
    type: ServiceErrorType,
    data: z.unknown().optional()
});
export function isServiceError(x) {
    return (ServiceError.safeParse(x).success);
}
export const ServiceStatusTypes = [
    "OK",
    "MINOR_ISSUES",
    "SCHEDULED_MAINTENANCE",
    "SEVERE_ISSUES"
];
export const ServiceStatus = z.enum(ServiceStatusTypes);
export const ServicePingInfo = z.object({
    environment: z.string().min(ServiceMinimumStringLength),
    version: z.string().min(ServiceMinimumStringLength),
    status: ServiceStatus,
    database: z.string().min(ServiceMinimumStringLength),
    mongoStatus: z.enum(["ok", "error"]),
    session: z.string().min(ServiceMinimumSessionIdLength)
});
export const UserRoleTypes = [
    "anonymous",
    "ephemeral",
    "listener",
    "donor",
    "amplifier",
];
export const UserRole = z.enum(UserRoleTypes);
export const AdminRoleTypes = [
    "none",
    "operator",
    "manager",
    "root",
];
export const AdminRole = z.enum(AdminRoleTypes);
export const UserLocation = z.object({
    id: z.string().min(ServiceMinimumObjectIdLength),
    value: z.string().optional()
});
export const ServiceUser = z.object({
    id: z.string().min(ServiceMinimumObjectIdLength),
    created: z.coerce.date(),
    modified: z.coerce.date(),
    suids: z.string().array(),
    role: UserRole,
    principal: z.string().email(),
    // BUGBUG: Temporary allowance for empty name until updateFullName fix is pushed and empty name users updated.
    name: z.string().min(0),
    emails: z.string().email().array(),
    mobile: z.string().optional(),
    language: z.string().min(ServiceMinimumStringLength),
    location: UserLocation.optional(),
    avatar: z.string().url().optional(),
    lastLogin: z.coerce.date()
});
export const StytchTrustedMetadata = z.object({
    adminRole: AdminRole
});
export const ServiceUserWithAdminInfo = ServiceUser.extend({
    adminRole: AdminRole
});
export const SU_AnonymousUserId = "user_anonymous";
export const SU_AnonymousUserPrincipal = ["anonymous@kexp.org"];
export const SU_AnonymousUserContact = ["anonymous@kexp.org"];
export const TrustedServiceUserSignupInfo = ServiceUser.pick({
    name: true,
    language: true,
    location: true
});
export const UntrustedServiceUserSignupInfo = z.object({
    verificationData: z.object({
        existingDonor: z.boolean(),
        ccValidation: z.string().min(ServiceMinimumCCValidationLength).optional(),
        postalCode: z.string().min(ServiceMinimumPostalCodeValidationLength).optional(),
    }).refine((input) => {
        if (input.existingDonor) {
            if (!input.postalCode) {
                return (false);
            }
            if (!input.ccValidation) {
                return (false);
            }
        }
        return (true);
    }),
    userData: ServiceUser.pick({
        emails: true,
        mobile: true
    })
});
export const ServiceUserSignupInfo = z.object({
    trusted: TrustedServiceUserSignupInfo,
    untrusted: UntrustedServiceUserSignupInfo.optional()
});
export const ServiceUserVerificationRequest = z.object({
    actionTag: z.string().min(ServiceMinimumStringLength),
    actionDescription: z.string().min(ServiceMinimumStringLength),
    additionalEmailContacts: z.string().email().array(),
    additionalMobileContacts: z.string().array()
});
export const ServiceUserEmailVerificationResponse = z.object({
    email: z.string().email(),
    code: z.string().min(ServiceMinimumVerificationCodeLength)
});
export const ServiceUserMobileVerificationResponse = z.object({
    mobile: z.string().min(ServiceMinimumMobileNumberLength),
    code: z.string().min(ServiceMinimumVerificationCodeLength)
});
export const ServiceUserVerificationResponse = z.object({
    principalResponse: ServiceUserEmailVerificationResponse,
    additionalEmailResponses: ServiceUserEmailVerificationResponse.array().optional(),
    additionalMobileResponses: ServiceUserMobileVerificationResponse.array().optional()
});
export const ServiceUserSupportRequest = z.object({
    serviceTag: z.string().min(ServiceMinimumStringLength),
    subject: z.string().min(ServiceMinimumStringLength),
    message: z.string().min(ServiceMinimumStringLength)
});
export const ServiceUserSupportResponse = z.object({
    serviceTag: z.string().min(ServiceMinimumStringLength),
    subject: z.string().min(ServiceMinimumStringLength),
});
export const ServiceUserDonorStatus = z.object({
    countConnectedPaymentProfiles: z.number(),
    countPaymentProfiles: z.number(),
    countConnectedActiveSubscriptions: z.number(),
    countActiveSubscriptions: z.number(),
    countConnectedActiveInExpirationNotificationWindowSubscriptions: z.number(),
    countActiveInExpirationNotificationWindowSubscriptions: z.number(),
    countConnectedPastDueSubscriptions: z.number(),
    countPastDueSubscriptions: z.number(),
    hasTransactions: z.boolean(),
    defaultConnectedPaymentProfile: z.string(),
});
export const SubscriptionTypes = [
    "pledge",
    "amplifier"
];
export const SubscriptionType = z.enum(SubscriptionTypes);
export const SubscriptionStatuses = [
    "inactive",
    "active",
    "pending"
];
export const SubscriptionStatus = z.enum(SubscriptionStatuses);
export const ServiceUserSubscription = z.object({
    id: z.string().min(ServiceMinimumBraintreeIdLength),
    type: SubscriptionType,
    status: SubscriptionStatus,
    createdDate: z.coerce.date(),
    currency: z.string().min(ServiceMinimumCurrencyLength),
    amount: z.number(),
    ccType: z.string().min(ServiceMinimumCCTypeLength),
    cc4: z.string().min(ServiceMinimumCCIdentificationLength),
    ccExpirationDate: z.coerce.date().optional(),
    ccIsInExpirationNotificationWindow: z.boolean(),
    nextPaymentDate: z.coerce.date(),
    paymentsLeft: z.number(),
    balancePastDue: z.number(),
    mostRecentTransactionDate: z.coerce.date().optional(),
    failureCount: z.number(),
});
export const ServiceUserSubscriberStatus = z.object({
    subscriptionStateId: z.string().min(ServiceMinimumStringLength),
    countConnectedActiveSubscriptions: z.number(),
    countConnectedActiveInExpirationNotificationWindowSubscriptions: z.number(),
    countConnectedPastDueSubscriptions: z.number(),
    subscriptions: ServiceUserSubscription.array(),
});
export const ServiceUserPaymentProfileClientToken = z.object({
    paymentProfile: z.string(),
    clientToken: z.string()
});
export const ServiceUserGiveNowRequest = z.object({
    currency: z.string().min(ServiceMinimumCurrencyLength),
    amount: z.number(),
    paymentNonce: z.string().min(ServiceMinimumPaymentNonce),
    deviceData: z.string().min(ServiceMinimumDeviceData).optional()
});
export const ServiceUserGiveMonthlyRequest = z.object({
    currency: z.string().min(ServiceMinimumCurrencyLength),
    amount: z.number(),
    paymentNonce: z.string().min(ServiceMinimumPaymentNonce)
});
export const GiveTransactionTypes = [
    "one-time",
    "monthly",
    "refund",
    "pending"
];
export const GiveTransactionType = z.enum(GiveTransactionTypes);
export const ServiceUserGiveTransaction = z.object({
    id: z.string().min(ServiceMinimumStringLength),
    type: GiveTransactionType,
    currency: z.string().min(ServiceMinimumCurrencyLength),
    amount: z.number(),
    cc4: z.string().min(ServiceMinimumCCIdentificationLength).optional(),
    timestamp: z.coerce.date(),
});
export const ServiceUserGivingHistory = z.object({
    paymentProfiles: z.string().array(),
    transactions: ServiceUserGiveTransaction.array(),
    transactionsStateId: z.string().min(ServiceMinimumStringLength),
});
export const ServiceUserBraintreeCustomer = z.object({
    userId: z.string().min(ServiceMinimumObjectIdLength),
    braintreeId: z.string().min(ServiceMinimumBraintreeIdLength),
    braintreeContact: z.string().email(),
    braintreeTimestamp: z.coerce.date(),
    braintreeBasedRole: UserRole,
    connectedOn: z.coerce.date(),
    asDefault: z.coerce.date(),
});
export const ServiceUserConnectCardRequest = z.object({
    cc4: z.string().min(ServiceMinimumCCIdentificationLength),
    postalCode: z.string().min(ServiceMinimumPostalCodeValidationLength)
});
export const ServiceUserConnectCardResponse = z.object({
    subcArray: ServiceUserBraintreeCustomer.array()
});
export const ServiceUserUpdateSubscriptionCardRequest = z.object({
    subscriptionId: z.string().min(ServiceMinimumBraintreeIdLength),
    paymentNonce: z.string().min(ServiceMinimumPaymentNonce),
    currency: z.string().min(ServiceMinimumCurrencyLength),
    amount: z.number(),
    deviceData: z.string().min(ServiceMinimumDeviceData).optional()
});
export const ServiceUserUpdateSubscriptionCardResponse = z.object({
    updatedSubscription: ServiceUserSubscription,
    additionalPaymentStatusCode: z.number(),
    additionalPaymentStatusInfo: z.string(),
});
export const ServiceUserFullProfile = z.object({
    donorProfile: ServiceUser,
    subscriberStatus: ServiceUserSubscriberStatus,
    givingHistory: ServiceUserGivingHistory
});
export const OIDCSignupEndpointConfig = z.object({
    userinfo_endpoint: z.string().url()
});
export async function getMicrosoftUserInfoEndpoint() {
    const oidcConfig = OIDCSignupEndpointConfig.parse(await (await fetch("https://login.microsoftonline.com/common/v2.0/.well-known/openid-configuration")).json());
    return (oidcConfig.userinfo_endpoint);
}
export const MicrosoftGraphUserInfo = z.object({
    name: z.string().optional(),
    givenname: z.string().optional(),
    familyname: z.string().optional(),
    picture: z.string().url().optional(),
    email: z.string().email()
});
export async function getMicrosoftUserInfo(endpoint, token) {
    const userInfo = MicrosoftGraphUserInfo.parse(await (await fetch(endpoint, {
        headers: {
            "Host": "graph.microsoft.com",
            "Authorization": "Bearer " + token
        }
    })).json());
    return (userInfo);
}
